import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Merge,
  Check,
  Loader2,
  Info,
  AlertTriangle,
  Smartphone,
  ArrowDown,
  ShieldCheck,
  ClipboardList,
} from 'lucide-react';
import { sendMergeVerifyCode } from '../../../../../api/register';
import type { MergeSmsVerification } from '../../../../../api/register';
import type { RegisterFormData, MatchedEntity } from '../../../../../stores/registerStore';
import { maskIdCard, maskPhone } from '../mask';
import { MatchedFieldBadges, ZoomAvatarButton, ZoomPreviewLayer } from '../shared';
import { useSmsCodeFlow } from '../../../../../hooks/useSmsCodeFlow';

/**
 * 身份合并弹窗（多重身份 → 主档收敛向导）
 *
 * 设计要点：
 * - 主档/次档分区呈现：主档案大卡片（自动锁定 + 合并后信息预览）在上，
 *   次档紧凑卡片在下，中间汇聚分隔明确合并方向；
 * - 合并后信息预览：以注册表单值优先展示（后端将以注册信息覆盖主档），
 *   与主档现值不同的字段标「更新」徽章，让用户预知覆盖结果；
 * - 已注册用户次档显示 amber 警示（合并后该账号将停用）；
 * - 提示说明分三层：锁定规则（顶部 teal 条）/ 合并动作清单（底部 slate 区）/ 逐字段悬停 detail。
 */
export function IdentityMergeModal({
  isOpen,
  onClose,
  entities,
  selectedId,
  onSelect,
  onMerge,
  onAppeal,
  primaryPhone,
  formData,
}: {
  isOpen: boolean;
  onClose: () => void;
  entities: MatchedEntity[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /**
   * 确认合并：verifications 为「仅联系方式命中」次档的短信验证结果
   * （姓名/身份证/邮箱自证的次档无需验证，无待验证项时传空数组）。
   * 服务端判定仍存在需短信验证的次档时返回 { requireSmsVerify }，
   * 弹窗据此进入验证子流程（以服务端判定为准）。
   */
  onMerge: (
    verifications: MergeSmsVerification[],
  ) => Promise<{ requireSmsVerify?: Array<{ secondaryId: string; phone: string }> } | undefined>;
  /** 无法接收验证码或对合并有疑问 → 人工申请复议 */
  onAppeal: () => void;
  primaryPhone: string;
  /** 注册表单：用于「合并后档案信息」预览（后端以注册填写为准覆盖主档） */
  formData?: RegisterFormData;
}) {
  // 身份唯一性宪法·主档自动锁定：
  //   打开合并向导时，严格按「注册主手机 == Party.phone」自动选中主档；
  //   若主手机仅命中于某条的 backup_phone，则 fallback 到该条。
  //   仅在弹窗打开瞬间执行一次，之后尊重用户手动更换主档案的选择
  //   （依赖含 selectedId 会导致用户每次点选次档都被强制改回）。
  useEffect(() => {
    if (!isOpen) return;
    const auto =
      entities?.find((e) => e.phone === primaryPhone) || entities?.find((e) => e.backupPhone === primaryPhone);
    if (auto) {
      onSelect(auto.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 头像放大预览（与认领弹窗一致：结合档案照片核对是否本人）
  const [zoomEntity, setZoomEntity] = useState<MatchedEntity | null>(null);
  useEffect(() => {
    if (!isOpen) setZoomEntity(null);
  }, [isOpen]);

  // ===== 合并前短信验证（姓名不同、仅联系方式命中的次档） =====
  // 该类次档（特别是注册填写的备用电话，未经三要素验证）可能是填错的他人号码，
  // 合并前须凭命中号码的短信验证码证明持有，避免误并入他人档案。
  // 判定：matchedFields 无姓名/身份证/邮箱命中 && 有电话命中（邮箱命中与身份证同级
  // 自证：注册邮箱已经邮箱验证码验证持有，与后端次档分级口径一致）。
  const needsVerifySecondaries = entities
    .filter((e) => e.id !== selectedId)
    .filter((e) => {
      const fields = e.matchedFields ?? [];
      // 仅「完全相同」的姓名算自证；音同/省略姓同音（homophone/abbreviated）属人工核对
      // 范畴，不作自动匹配依据——此类次档必须走短信验证（对齐产品规则）
      const hasName = fields.some((m) => m.field === 'fullName' && m.matchWay === 'exact');
      const hasIdCard = fields.some((m) => m.field === 'idCard');
      const hasEmail = fields.some((m) => m.field === 'email');
      const hasPhone = fields.some((m) => m.field === 'phone' || m.field === 'backupPhone');
      return !hasName && !hasIdCard && !hasEmail && hasPhone;
    });
  // 验证号码 = 注册填写的备用联系方式（合并风险锚点：主手机经三要素验证天然自证，
  // 备用电话未经持有验证，可能填错并登记在其他档案上）；未填备用电话时回退为
  // 与该次档交叉命中的注册号码
  const hitPhoneOf = (e: MatchedEntity) => {
    if (formData?.backupPhone) return formData.backupPhone;
    return [formData?.phone].find((p) => !!p && (p === e.phone || p === e.backupPhone)) ?? '';
  };

  const [verifyMode, setVerifyMode] = useState(false);
  const [verifyIndex, setVerifyIndex] = useState(0);
  const [verifications, setVerifications] = useState<MergeSmsVerification[]>([]);
  const [smsCode, setSmsCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  // 发送锁 + 60s 重发冷却（公共 Hook）
  const { countdown, sending, send: sendCode, setCountdown } = useSmsCodeFlow();

  // 验证子流程整体重置：弹窗关闭或用户更换主档（次档集合随之变化）时调用，
  // 避免 verifyIndex/verifications 错位指向其他档案（已提交的验证码在后端即发即消费，
  // 旧记录保留会在合并时重复提交失效码）
  const resetVerifyState = () => {
    setVerifyMode(false);
    setVerifyIndex(0);
    setVerifications([]);
    setSmsCode('');
    setCountdown(0);
    setVerifyError('');
  };

  // 弹窗关闭时重置验证子流程
  useEffect(() => {
    if (!isOpen) resetVerifyState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 换主档入口：重置验证子流程后再切主档
  const handleSelect = (id: string) => {
    resetVerifyState();
    onSelect(id);
  };


  const currentVerifyTarget = verifyMode ? (needsVerifySecondaries[verifyIndex] ?? null) : null;
  const currentVerifyPhone = currentVerifyTarget ? hitPhoneOf(currentVerifyTarget) : '';

  const handleEnterVerify = async () => {
    if (needsVerifySecondaries.length === 0) {
      // 全部次档姓名/身份证/邮箱自证通过，走原合并流程；
      // 服务端判定仍存在需验证次档时（口径差异兜底），进入验证子流程以服务端为准
      try {
        const result = await onMerge([]);
        if (result?.requireSmsVerify?.length) {
          resetVerifyState();
          setVerifyMode(true);
        }
      } catch {
        /* 合并失败已在 onMerge 内 toast 提示 */
      }
      return;
    }
    setVerifyMode(true);
    setVerifyError('');
  };

  const handleSendVerifyCode = async () => {
    if (!currentVerifyTarget || !currentVerifyPhone) return;
    setVerifyError('');
    try {
      await sendCode(async () => {
        const res = await sendMergeVerifyCode({
          secondaryId: currentVerifyTarget.id,
          phone: currentVerifyPhone,
          registerData: {
            fullName: formData?.fullName,
            phone: formData?.phone,
            backupPhone: formData?.backupPhone,
            idCard: formData?.idCard,
            email: formData?.email,
          },
        });
        if (!res.success) {
          throw new Error(res.message || '验证码发送失败，请稍后重试');
        }
      });
    } catch (e) {
      setVerifyError(e instanceof Error ? e.message : '验证码发送失败，请稍后重试');
    }
  };

  const handleVerifyAndMerge = async () => {
    if (!currentVerifyTarget || !currentVerifyPhone || !smsCode.trim() || submitting) return;
    setSubmitting(true);
    setVerifyError('');
    try {
      const next: MergeSmsVerification[] = [
        ...verifications,
        { secondaryId: currentVerifyTarget.id, phone: currentVerifyPhone, code: smsCode.trim() },
      ];
      if (verifyIndex + 1 < needsVerifySecondaries.length) {
        // 多个待验证次档：逐个验证，全部通过后统一发起合并
        setVerifications(next);
        setVerifyIndex(verifyIndex + 1);
        setSmsCode('');
        setCountdown(0);
        return;
      }
      const result = await onMerge(next);
      if (result?.requireSmsVerify?.length) {
        // 服务端判定与本地不一致或验证记录已失效：以服务端为准重新走验证子流程
        resetVerifyState();
        setVerifyMode(true);
      }
    } catch {
      /* 合并失败已在 onMerge 内 toast 提示，保持验证视图供用户重试 */
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const primary = entities.find((e) => e.id === selectedId) ?? null;
  const secondaries = entities.filter((e) => e.id !== selectedId);

  // 合并后档案信息预览：注册填写值优先（后端以此覆盖主档），与主档现值不同的字段标「更新」
  // previewContactRows：姓名与联系方式（第一行）；previewOtherRows：邮箱与身份证（第二行）
  type PreviewRow = { label: string; value: string; overridden: boolean };
  const previewContactRows: PreviewRow[] = primary
    ? [
        {
          label: '姓名',
          value: formData?.fullName || primary.fullName,
          overridden: !!formData?.fullName && formData.fullName !== primary.fullName,
        },
        {
          label: '手机号',
          value: formData?.phone || primary.phone,
          overridden: !!formData?.phone && formData.phone !== primary.phone,
        },
        ...(formData?.backupPhone || primary.backupPhone
          ? [
              {
                label: '备用电话',
                value: formData?.backupPhone || primary.backupPhone!,
                overridden: !!formData?.backupPhone && formData.backupPhone !== primary.backupPhone,
              },
            ]
          : []),
      ]
    : [];
  const previewOtherRows: PreviewRow[] = primary
    ? [
        ...(formData?.email || primary.email
          ? [
              {
                label: '邮箱',
                value: formData?.email || primary.email!,
                // 邮箱一律小写落库，故只比归一后的值，避免大小写差异被标成「有变更」
                overridden:
                  !!formData?.email &&
                  formData.email.trim().toLowerCase() !== (primary.email ?? '').trim().toLowerCase(),
              },
            ]
          : []),
        ...(formData?.idCard || primary.idCard
          ? [
              {
                label: '身份证',
                  value: maskIdCard(formData?.idCard || primary.idCard),
                overridden: !!formData?.idCard && formData.idCard !== primary.idCard,
              },
            ]
          : []),
      ]
    : [];

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[560px] mx-4 max-h-[82vh] flex flex-col animate-[merge-modal-in_0.22s_cubic-bezier(0.16,1,0.3,1)]">
        {/* 头部：品牌 teal 渐变图标（与认领弹窗视觉语言统一） */}
        <div className="p-6 pb-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-md shadow-teal-500/20">
              <Merge className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">合并重复身份</h3>
              <p className="text-sm text-slate-500">确认后其余档案将并入主档案，确保系统内一人一档</p>
            </div>
          </div>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {!verifyMode ? (
            <>
          {/* 锁定规则说明（teal 信息条） */}
          <div className="p-3.5 bg-teal-50 border border-teal-200 rounded-lg flex gap-3">
            <ShieldCheck className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
            <p className="text-sm text-teal-800 leading-relaxed">
              检测到系统内有 <span className="font-semibold">{entities.length}</span>{' '}
              条与您关联的身份记录，已按您填写的注册手机号 <span className="font-semibold">自动锁定主档案</span>。
              请核对档案照片与信息；如锁定不符，可点击其他档案更换主档案。
            </p>
          </div>
          {/* ===== 主档案区 ===== */}
          {primary && (
            <section>
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                <h4 className="text-xs font-semibold text-slate-500 tracking-wide">保留的主档案</h4>
                <div className="flex-1 border-t border-dashed border-slate-200" />
              </div>
              <div className="relative p-4 rounded-xl border-2 border-teal-500 bg-teal-50/40">
                <span className="absolute -top-2.5 right-4 px-2.5 py-0.5 bg-teal-500 text-white text-[11px] rounded-full font-medium shadow-sm shadow-teal-500/30">
                  主档案 · 自动锁定
                </span>
                <div className="flex items-center gap-4 mt-1">
                  <ZoomAvatarButton
                    name={primary.fullName}
                    avatarUrl={primary.avatar}
                    size="xl"
                    ring="ring-2 ring-teal-100"
                    onZoom={() => setZoomEntity(primary)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-lg font-semibold text-slate-800">{primary.fullName}</span>
                      {primary.username && (
                        <span
                          className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full"
                          title="该档案已注册账号，合并后将继续作为您的登录账号使用"
                        >
                          已注册用户
                        </span>
                      )}
                      <MatchedFieldBadges fields={primary.matchedFields} />
                    </div>
                    <div className="text-sm text-slate-500 truncate">
                      {[primary.phone, primary.backupPhone && `备用 ${primary.backupPhone}`, primary.email]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                </div>
                {/* 合并后档案信息预览（以注册填写为准，覆盖字段标「更新」） */}
                {formData && (previewContactRows.length > 0 || previewOtherRows.length > 0) && (
                  <div className="mt-4 pt-3 border-t border-teal-200/60">
                    <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5">
                      <ClipboardList className="w-3.5 h-3.5 text-slate-400" />
                      合并后档案信息
                      <span className="text-teal-600">（以本次注册填写为准）</span>
                    </p>
                    <div className="space-y-1 text-sm">
                      {[previewContactRows, previewOtherRows]
                        .filter((rows) => rows.length > 0)
                        .map((rows, rowIdx) => (
                          <div key={rowIdx} className="flex items-center gap-x-5 gap-y-0.5 flex-wrap">
                            {rows.map((row) => (
                              <span key={row.label} className="flex items-center gap-1.5 min-w-0">
                                <span className="text-slate-400 shrink-0">{row.label}</span>
                                <span
                                  className={`truncate ${
                                    row.overridden ? 'text-teal-700 font-medium' : 'text-slate-700'
                                  }`}
                                >
                                  {row.value}
                                  {row.overridden && (
                                    <span className="ml-1.5 text-[10px] px-1.5 py-px bg-teal-500 text-white rounded-full align-middle">
                                      更新
                                    </span>
                                  )}
                                </span>
                              </span>
                            ))}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
          {/* ===== 汇聚分隔：合并方向可视化 ===== */}
          {secondaries.length > 0 && (
            <div className="flex items-center gap-3" aria-hidden>
              <div className="flex-1 border-t border-dashed border-slate-200" />
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <ArrowDown className="w-4 h-4 text-teal-500" />
                以下 <span className="font-semibold text-slate-700">{secondaries.length}</span>{' '}
                条档案将并入主档案
              </div>
              <div className="flex-1 border-t border-dashed border-slate-200" />
            </div>
          )}
          {/* ===== 次档区（点击卡片可更换主档案） ===== */}
          {secondaries.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                <h4 className="text-xs font-semibold text-slate-500 tracking-wide">
                  并入的档案
                  <span className="ml-1.5 font-normal text-slate-400">点击卡片可更换主档案</span>
                </h4>
                <div className="flex-1 border-t border-dashed border-slate-200" />
              </div>
              <div className="space-y-2.5">
                {secondaries.map((entity) => (
                  <div
                    key={entity.id}
                    onClick={() => handleSelect(entity.id)}
                    className="p-3.5 rounded-xl border border-slate-200 bg-white hover:border-teal-300 hover:bg-teal-50/30 cursor-pointer transition-all group"
                    title="点击将此档案设为主档案"
                  >
                    <div className="flex items-center gap-3">
                      <ZoomAvatarButton
                        name={entity.fullName}
                        avatarUrl={entity.avatar}
                        size="md"
                        ring="ring-2 ring-slate-100"
                        scope="avatar"
                        onZoom={() => setZoomEntity(entity)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-medium text-slate-700">{entity.fullName}</span>
                          {entity.username && (
                            <span
                              className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full"
                              title="该档案已注册账号；合并后此账号将停止使用，订单与关联信息转入主档案"
                            >
                              已注册用户
                            </span>
                          )}
                          <MatchedFieldBadges fields={entity.matchedFields} max={2} variant="secondary" />
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {[
                            entity.phone,
                            entity.backupPhone && `备用 ${entity.backupPhone}`,
                            entity.email,
                            entity.idCard && `身份证 ${maskIdCard(entity.idCard)}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </div>
                      <span className="shrink-0 flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-400 text-xs rounded-md group-hover:bg-teal-100 group-hover:text-teal-600 transition-colors">
                        <Merge className="w-3.5 h-3.5" />
                        并入
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {/* ===== 合并后影响说明 ===== */}
          <div className="p-4 bg-slate-50 rounded-xl">
            <p className="text-xs font-semibold text-slate-500 mb-2.5 flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5 text-slate-400" />
              确认合并后将执行以下操作
            </p>
            <ul className="space-y-2 text-sm text-slate-600 leading-relaxed">
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />
                各档案的历史订单、客户关联与标签将统一归入主档案
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />
                主档案的联系方式与身份信息将以本次注册填写为准更新
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />
                其余档案将标记为「已合并」并停止使用；如发现有非本人档案，建议先联系客服核实申诉
              </li>
            </ul>
          </div>
            </>
          ) : (
            /* ===== 验证视图：姓名不同仅号码命中次档的短信验证 ===== */
            <div className="p-4 bg-amber-50/60 border border-amber-200 rounded-xl">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                  <Smartphone className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-800">合并安全验证</h4>
                  <p className="text-xs text-slate-500">联系方式相同但姓名不同的档案，验证号码归属后才能合并</p>
                </div>
              </div>
              <p className="text-sm text-amber-800 bg-white/70 rounded-lg p-3 border border-amber-100 leading-relaxed mb-3">
                档案「<span className="font-semibold">{currentVerifyTarget?.fullName}</span>
                」仅凭联系方式与您的注册信息匹配（姓名不同）。该联系方式可能是填错的他人号码，请输入{' '}
                <span className="font-semibold">{maskPhone(currentVerifyPhone)}</span>{' '}
                收到的短信验证码，确认号码归属后方可并入。
              </p>
              {/* 待验证进度（多个次档时逐个验证） */}
              {needsVerifySecondaries.length > 1 && (
                <div className="space-y-1.5 mb-3">
                  {needsVerifySecondaries.map((e, idx) => {
                    const done = idx < verifyIndex;
                    const active = idx === verifyIndex;
                    return (
                      <div
                        key={e.id}
                        className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                          active ? 'border-amber-300 bg-white' : 'border-slate-200 bg-white/60'
                        }`}
                      >
                        <span className="flex-1 truncate text-slate-700">
                          {e.fullName} · {maskPhone(hitPhoneOf(e))}
                        </span>
                        {done ? (
                          <span className="flex items-center gap-1 text-teal-600 text-xs font-medium">
                            <Check className="w-3.5 h-3.5" />
                            已验证
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">{active ? '验证中' : '待验证'}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* 验证码输入 + 发送 */}
              <div className="flex gap-2">
                <input
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6 位短信验证码"
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white"
                />
                <button
                  type="button"
                  onClick={handleSendVerifyCode}
                  disabled={sending || countdown > 0}
                  className="shrink-0 px-4 py-2.5 border border-teal-500 text-teal-700 text-sm font-medium rounded-lg hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : countdown > 0 ? (
                    `${countdown}s 后重发`
                  ) : (
                    '发送验证码'
                  )}
                </button>
              </div>
              {verifyError && (
                <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {verifyError}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="p-6 pt-4 border-t border-slate-100 flex-shrink-0">
          <div className="flex gap-3">
            {verifyMode ? (
              <>
                <button
                  onClick={resetVerifyState}
                  className="flex-1 py-3 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  返回
                </button>
                <button
                  onClick={handleVerifyAndMerge}
                  disabled={!smsCode.trim() || sending || submitting}
                  className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-teal-600/30 flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  验证并合并{primary ? `（保留「${primary.fullName}」）` : ''}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  暂不合并
                </button>
                <button
                  onClick={handleEnterVerify}
                  disabled={!selectedId}
                  className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-teal-600/30"
                >
                  确认合并{primary ? `（保留「${primary.fullName}」）` : ''}
                </button>
              </>
            )}
          </div>
          {/* 人工复议入口：无法接收验证码或对合并结果有疑问 */}
          <button
            type="button"
            onClick={onAppeal}
            className="w-full mt-3 text-center text-xs text-slate-400 hover:text-teal-600 transition-colors"
          >
            无法接收短信或对合并有疑问？
            <span className="font-medium text-slate-500 underline underline-offset-2 hover:text-teal-600">
              申请人工复议
            </span>
          </button>
        </div>
        {/* 头像放大预览（与认领弹窗一致：结合档案照片核对是否本人） */}
        {zoomEntity?.avatar && (
          <ZoomPreviewLayer
            src={zoomEntity.avatar}
            alt={zoomEntity.fullName}
            onClose={() => setZoomEntity(null)}
          />
        )}
      </div>
    </div>,
    document.body
  );
}